import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../types/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Logo from './Logo';

const LoginPage: React.FC = () => {
  useDocumentTitle('Login');
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(credentials);
      navigate('/users');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-[#111121] dark group/design-root overflow-x-hidden">
      <div className="w-full max-w-md p-8 space-y-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        {/* Header */}
        <div className="text-center">
          <h1 className="text-white tracking-tight text-[32px] font-bold leading-tight font-display">
            Admin Login
          </h1>
          <p className="text-muted mt-2 font-display">
            Welcome back, please enter your details.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username Field */}
          <div className="flex flex-col min-w-40 flex-1">
            <label className="text-white text-base font-medium leading-normal pb-2 font-display" htmlFor="username">
              Username
            </label>
            <input
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-white focus:outline-0 focus:ring-2 focus:ring-primary border-none bg-dialog-border h-14 placeholder:text-muted p-4 text-base font-normal leading-normal transition-shadow duration-200 font-display"
              id="username"
              name="username"
              placeholder="Enter your username"
              value={credentials.username}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
          </div>

          {/* Password Field */}
          <div className="flex flex-col min-w-40 flex-1">
            <label className="text-white text-base font-medium leading-normal pb-2 font-display" htmlFor="password">
              Password
            </label>
            <div className="relative flex w-full flex-1 items-stretch rounded-lg">
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-white focus:outline-0 focus:ring-2 focus:ring-primary border-none bg-dialog-border h-14 placeholder:text-muted p-4 pr-12 text-base font-normal leading-normal transition-shadow duration-200 font-display"
                id="password"
                name="password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={handleChange}
                disabled={isLoading}
                required
              />
              <div 
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-muted cursor-pointer hover:text-white transition-colors duration-200"
                onClick={togglePasswordVisibility}
              >
                <span className="material-symbols-outlined">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </div>
            </div>
          </div>

          {/* Login Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading || !credentials.username || !credentials.password}
              className="flex min-w-[84px] max-w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 flex-1 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#111121] focus:ring-primary transition-colors duration-200 font-display disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="truncate">
                {isLoading ? 'Logging in...' : 'Login'}
              </span>
            </button>
          </div>
        </form>

        {/* Forgot Password Link */}
        <p className="text-muted text-sm font-normal leading-normal text-center font-display">
          <a className="underline hover:text-white transition-colors duration-200" href="#">
            Forgot Password?
          </a>
        </p>

        {/* Divider */}
        <div className="flex items-center justify-center space-x-2">
          <div className="h-px bg-dialog-border flex-grow"></div>
          <span className="text-muted text-sm font-display">OR</span>
          <div className="h-px bg-dialog-border flex-grow"></div>
        </div>

        {/* OAuth Buttons */}
        <div className="flex justify-center space-x-4">
          <button 
            type="button"
            className="p-3 bg-dialog-border rounded-full text-white hover:bg-primary/20 transition-colors duration-200"
            disabled
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"></path>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"></path>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"></path>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"></path>
            </svg>
          </button>
          <button 
            type="button"
            className="p-3 bg-dialog-border rounded-full text-white hover:bg-primary/20 transition-colors duration-200"
            disabled
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1.39C7.26 1.39 3.4 5.25 3.4 9.99c0 2.45.98 4.7 2.65 6.42a10.82 10.82 0 01-1.44 1.83 1.55 1.55 0 00-.47 1.15c0 .7.56 1.28 1.27 1.28.5 0 .97-.28 1.21-.7.42-.71.97-1.32 1.63-1.85.8.19 1.64.29 2.5.29 4.74 0 8.6-3.86 8.6-8.6S16.74 1.39 12 1.39zm3.83 8.35c.23.41.23.9-.02 1.3a1.47 1.47 0 01-1.34.73h-1.3v2.82c0 .6-.48 1.08-1.08 1.08s-1.08-.48-1.08-1.08v-2.82H9.72c-.6 0-1.08-.48-1.08-1.08s.48-1.08 1.08-1.08h1.28V7.57c0-.6.48-1.08 1.08-1.08s1.08.48 1.08 1.08v1.09h.6c.48 0 .9.31 1.05.78z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;